<template>
  <div class="question-bank-detail">
    <!-- 加载状态 -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">加载中</span>
      </div>
      <p class="mt-3 text-muted">正在加载题库详情...</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="text-center py-5">
      <div class="alert alert-danger">
        <i class="bi bi-exclamation-triangle me-2"></i>
        {{ error }}
      </div>
      <button class="btn btn-outline-primary" @click="loadQuestionBank">
        <i class="bi bi-arrow-clockwise me-1"></i>
        重试
      </button>
    </div>

    <!-- 题库详情内容 -->
    <div v-else-if="questionBank">
      <!-- 页面标题 -->
      <div class="page-header">
        <div>
          <h1>题库详情</h1>
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
              <li class="breadcrumb-item">
                <router-link to="/admin/question-banks">题库管理</router-link>
              </li>
              <li class="breadcrumb-item active">{{ questionBank.subdomain }}</li>
            </ol>
          </nav>
        </div>
        <div class="header-actions">
          <router-link :to="`/admin/question-banks/${questionBank.id}/edit`" class="btn btn-outline-warning">
            <i class="bi bi-pencil me-1"></i>
            编辑题库
          </router-link>
          <router-link :to="`/admin/question-banks/${questionBank.id}/cases`" class="btn btn-outline-info">
            <i class="bi bi-collection me-1"></i>
            案例题管理
          </router-link>
          <router-link :to="`/admin/question-banks/${questionBank.id}/questions/new`" class="btn btn-primary">
            <i class="bi bi-plus-lg me-1"></i>
            添加题目
          </router-link>
        </div>
      </div>

      <!-- 题库信息卡片 -->
      <div class="card mb-4">
        <div class="card-header">
          <h5 class="mb-0">题库信息</h5>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-md-6">
              <dl class="row mb-0">
                <dt class="col-sm-4">领域</dt>
                <dd class="col-sm-8">{{ questionBank.domain }}</dd>
                
                <dt class="col-sm-4">子领域</dt>
                <dd class="col-sm-8">{{ questionBank.subdomain }}</dd>
                
                <dt class="col-sm-4">代码</dt>
                <dd class="col-sm-8">{{ questionBank.subdomainCode }}</dd>
                
                <dt class="col-sm-4">版本</dt>
                <dd class="col-sm-8">{{ questionBank.version }}</dd>
              </dl>
            </div>
            <div class="col-md-6">
              <dl class="row mb-0">
                <dt class="col-sm-4">题目数量</dt>
                <dd class="col-sm-8">{{ questionBank.totalQuestions }} 道</dd>
                
                <dt class="col-sm-4">导入时间</dt>
                <dd class="col-sm-8">{{ formatDate(questionBank.importTime) }}</dd>
                
                <dt class="col-sm-4">创建时间</dt>
                <dd class="col-sm-8">{{ formatDate(questionBank.createdAt) }}</dd>
                
                <dt class="col-sm-4">更新时间</dt>
                <dd class="col-sm-8">{{ formatDate(questionBank.updatedAt) }}</dd>
              </dl>
            </div>
          </div>
          <div v-if="questionBank.description" class="mt-3">
            <h6>描述</h6>
            <p class="text-muted">{{ questionBank.description }}</p>
          </div>
        </div>
      </div>

      <!-- 题目列表 -->
      <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">题目列表</h5>
          <div class="d-flex gap-2">
            <select 
              class="form-select form-select-sm" 
              data-test="chapter-filter"
              v-model="selectedChapter"
              @change="filterQuestions"
            >
              <option value="">所有章节</option>
              <option v-for="chapter in chapters" :key="chapter" :value="chapter">
                {{ chapter }}
              </option>
            </select>
            <select class="form-select form-select-sm" v-model="selectedType" @change="filterQuestions">
              <option value="">所有题型</option>
              <option value="singleChoice">单选题</option>
              <option value="multipleChoice">多选题</option>
              <option value="trueFalse">判断题</option>
              <option value="boolean">判断题(旧)</option>
              <option value="essay">简答题</option>
              <option value="shortAnswer">简答题(旧)</option>
              <option value="calculation">计算题</option>
              <option value="fillBlank">填空题</option>
            </select>
          </div>
        </div>
        <div class="card-body">
          <!-- 空状态 -->
          <div v-if="filteredQuestions.length === 0" class="text-center py-4">
            <i class="bi bi-journal-x" style="font-size: 3rem; color: #ccc;"></i>
            <p class="text-muted mt-3">暂无题目</p>
            <router-link :to="`/admin/question-banks/${questionBank.id}/questions/new`" class="btn btn-primary">
              <i class="bi bi-plus-lg me-1"></i>
              添加题目
            </router-link>
          </div>

          <!-- 题目列表 -->
          <div v-else class="question-list">
            <div 
              v-for="(question, index) in filteredQuestions" 
              :key="question.id"
              class="question-item border rounded p-3 mb-3"
              data-test="question-item"
            >
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div class="flex-grow-1">
                  <span class="badge bg-primary me-2">{{ index + 1 }}</span>
                  <span class="badge bg-secondary me-2">{{ getQuestionTypeText(question.type) }}</span>
                  <span class="badge bg-info">{{ question.chapter }}</span>
                </div>
                <div class="action-buttons" v-if="isAdmin">
                  <button 
                    class="btn btn-sm btn-warning me-2"
                    data-test="edit-question-btn"
                    @click="editQuestion(question)"
                    title="编辑题目"
                  >
                    <i class="bi bi-pencil"></i>
                    <span class="d-none d-md-inline">编辑</span>
                  </button>
                  <button 
                    class="btn btn-sm btn-danger"
                    data-test="delete-question-btn"
                    @click="confirmDeleteQuestion(question)"
                    title="删除题目"
                  >
                    <i class="bi bi-trash"></i>
                    <span class="d-none d-md-inline">删除</span>
                  </button>
                </div>
              </div>
              
              <div class="question-content">
                <h6 class="mb-2">{{ question.question }}</h6>
                
                <div v-if="question.options" class="mb-2">
                  <div v-for="(option, key) in question.options" :key="key" class="form-check">
                    <input 
                      type="radio" 
                      :name="`question-${question.id}`" 
                      :id="`option-${question.id}-${key}`"
                      class="form-check-input"
                      disabled
                    />
                    <label :for="`option-${question.id}-${key}`" class="form-check-label">
                      {{ key }}. {{ option }}
                    </label>
                  </div>
                </div>
                
                <div class="mb-2">
                  <strong>答案：</strong>
                  <span class="text-success">{{ formatAnswer(question.answer) }}</span>
                </div>
                
                <div v-if="question.explanation" class="mb-2">
                  <strong>解析：</strong>
                  <span class="text-muted">{{ question.explanation }}</span>
                </div>
                
                <div class="d-flex gap-3">
                  <small class="text-muted">
                    <i class="bi bi-star-fill text-warning"></i>
                    难度：{{ getDifficultyText(question.difficulty) }}
                  </small>
                  <small class="text-muted">
                    <i class="bi bi-check-circle" :class="question.isActive ? 'text-success' : 'text-danger'"></i>
                    {{ question.isActive ? '启用' : '禁用' }}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { questionBanksApi } from '@/api/questionBanks'
import type { QuestionBank, Question } from '@/types'
import { useAuthStore } from '@/stores/auth'
import { getQuestionTypeText } from '@/utils/questionTypes'

const route = useRoute()
const router = useRouter()
const toast = useToast()
const authStore = useAuthStore()

// 状态
const questionBank = ref<QuestionBank | null>(null)
const questions = ref<Question[]>([])
const loading = ref(false)
const error = ref('')
const selectedChapter = ref('')
const selectedType = ref('')

// 计算属性
const isAdmin = computed(() => authStore.isAdmin)

const chapters = computed(() => {
  const uniqueChapters = new Set(questions.value.map(q => q.chapter))
  return Array.from(uniqueChapters).sort()
})

const filteredQuestions = computed(() => {
  return questions.value.filter(question => {
    const matchChapter = !selectedChapter.value || question.chapter === selectedChapter.value
    const matchType = !selectedType.value || question.type === selectedType.value
    return matchChapter && matchType
  })
})

// 方法
const loadQuestionBank = async () => {
  const id = Number(route.params.id)
  if (!id) {
    error.value = '无效的题库ID'
    return
  }

  loading.value = true
  error.value = ''

  try {
    const [bankResponse, questionsResponse] = await Promise.all([
      questionBanksApi.getQuestionBank(id),
      questionBanksApi.getQuestionsByBank(id)
    ])

    questionBank.value = bankResponse
    questions.value = questionsResponse
  } catch (err) {
    error.value = err instanceof Error ? err.message : '加载题库失败'
    toast.error('加载题库失败，请稍后重试')
  } finally {
    loading.value = false
  }
}

const editQuestion = (question: Question) => {
  const bankId = route.params.id;
  router.push(`/admin/question-banks/${bankId}/questions/${question.id}/edit`)
}

const confirmDeleteQuestion = async (question: Question) => {
  if (confirm(`确定要删除题目 "${question.question}" 吗？此操作不可恢复。`)) {
    try {
      await questionBanksApi.deleteQuestion(question.id)
      toast.success('题目删除成功')
      loadQuestionBank()
    } catch (err) {
      toast.error('删除题目失败')
    }
  }
}

const filterQuestions = () => {
  // 筛选逻辑已在计算属性中处理
}

// getQuestionTypeText 函数已从 @/utils/questionTypes 导入

const getDifficultyText = (difficulty: number | string) => {
  // 处理数字类型的难度值
  if (typeof difficulty === 'number') {
    const difficultyMap: Record<number, string> = {
      1: '简单',
      2: '中等', 
      3: '困难'
    }
    return difficultyMap[difficulty] || `难度${difficulty}`
  }
  
  // 处理字符串类型的难度值（向后兼容）
  const difficultyMap: Record<string, string> = {
    easy: '简单',
    medium: '中等',
    hard: '困难'
  }
  return difficultyMap[difficulty] || difficulty
}

const formatAnswer = (answer: any) => {
  if (Array.isArray(answer)) {
    return answer.join(', ')
  }
  if (typeof answer === 'boolean') {
    return answer ? '正确' : '错误'
  }
  return String(answer)
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('zh-CN')
}

// 生命周期
onMounted(() => {
  loadQuestionBank()
})
</script>

<style scoped>
.question-bank-detail {
  padding: 2rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.breadcrumb {
  margin-bottom: 0;
}

.question-item {
  transition: all 0.3s ease;
}

.question-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.question-content {
  margin-left: 3rem;
}

@media (max-width: 768px) {
  .question-bank-detail {
    padding: 1rem;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .question-content {
    margin-left: 0;
  }
}
</style>
